import { useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';

import CrossIcon from '@common/assets/icons/cross.svg?react';
import SendIcon from '@common/assets/icons/send.svg?react';
import { useAppStore } from '@common/stores/useAppStore';
import { Badge } from '@common/ui/Badge';
import { IconButton } from '@common/ui/buttons/IconButton';
import { Input } from '@common/ui/inputs/Input';
import { useEventEmit } from '@common/utils/events';

import {
  useGetMatchByUserId,
  usePostMessage,
} from '@services/hooks/MatchesService';
import { refreshMatchesStore } from '@services/StoreRefreshService';
import { type User } from '@services/UsersService';

import styles from './MatchDialog.module.scss';

const MESSAGE_TEMPLATES = [
  {
    testId: 'match-dialog-template-how-is-day',
    text: 'Как день?',
  },
  {
    testId: 'match-dialog-template-date',
    text: 'Пойдём на свидание?',
  },
  {
    testId: 'match-dialog-template-wave',
    text: '👋🏻',
  },
];

export type MatchDialogProps = {
  matchProfile: User;
  onClose: () => void;
};

export const MatchDialog: React.FC<MatchDialogProps> = ({
  matchProfile,
  onClose,
}) => {
  const store = useAppStore();
  const emit = useEventEmit();
  const navigate = useNavigate();

  const [message, setMessage] = useState(MESSAGE_TEMPLATES[0].text);

  const { trigger: triggerMatchByUserId, isMutating: isMutatingMatchByUserId } =
    useGetMatchByUserId();
  const { trigger: triggerPostMessage, isMutating: isMutatingPostMessage } =
    usePostMessage();

  useEffect(() => {
    refreshMatchesStore(store);
  }, []);

  const handleSendMessage = async (message: string) => {
    try {
      const { matchId } = await triggerMatchByUserId({
        userId: matchProfile.id,
      });

      await triggerPostMessage({
        matchId,
        content: message,
      });

      navigate({
        to: '/chats/$matchId',
        params: {
          matchId: matchId,
        },
      });
    } catch (e) {
      emit('error', {
        title: 'Ошибка',
        description: 'Не удалось отправить сообщение',
      });
    }
  };

  return (
    <motion.div
      className={styles.container}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.imageContainer}>
        <img src={matchProfile.photos[0]} className={styles.image} />
      </div>

      <div className={styles.contentContainer}>
        <div
          className={styles.closeContainer}
          data-test-id="match-dialog-close-control"
          onClick={onClose}
        >
          <CrossIcon />
        </div>

        <div className={styles.infoContainer}>
          <div className={styles.textBlock}>
            <span className={styles.title}>Вау, это мэтч!</span>
            <div className={styles.subtitle}>
              {Number(matchProfile.sex) === 1 ? (
                <>
                  <span>{matchProfile.username} тоже выбрала вас</span>
                  <span>Спроси её о чем-нибудь интересном</span>
                </>
              ) : (
                <>
                  <span>{matchProfile.username} тоже выбрал вас</span>
                  <span>Спроси его о чем-нибудь интересном</span>
                </>
              )}
            </div>
          </div>

          <div className={styles.messageBlock}>
            <div className={styles.messageInputWrapper}>
              <Input
                data-test-id="match-dialog-input"
                onChange={(e) => setMessage(e.target.value)}
                value={message}
              />

              <IconButton
                testId="match-dialog-send-button"
                variant="lightPrimary"
                className={styles.sendButton}
                onClick={() => handleSendMessage(message)}
                loading={isMutatingMatchByUserId || isMutatingPostMessage}
              >
                <SendIcon />
              </IconButton>
            </div>

            <div className={styles.templates}>
              {MESSAGE_TEMPLATES.map((item, index) => (
                <Badge
                  key={index}
                  testId={item.testId}
                  onClick={() => setMessage(item.text)}
                  color="neutralPrimary"
                  text={item.text}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
